"use client";
import { Check } from "@/components/icons";
import ShareButton from "@/components/ui/share-button";

export function StatsBlock({ stats }: { stats: Array<{ value: string; label: string }> }) {
  return (
    <div
      className="my-7 overflow-hidden rounded-2xl border sm:my-10"
      style={{
        borderColor: "color-mix(in srgb, var(--foreground) 6%, transparent)",
        background: "color-mix(in srgb, var(--surface-1) 45%, var(--background))",
      }}
    >
      <div
        className="divide-foreground/6 grid divide-y sm:divide-x sm:divide-y-0"
        style={{ gridTemplateColumns: `repeat(${stats.length}, 1fr)` }}
      >
        {stats.map((s) => (
          <div key={s.value} className="flex flex-col gap-1 px-6 py-5 sm:px-8 sm:py-7">
            <span className="text-foreground/75 text-3xl font-black tracking-tight sm:text-4xl">
              {s.value}
            </span>
            <span className="text-muted-foreground text-sm">{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PullQuote({ text, attribution }: { text: string; attribution: string }) {
  return (
    <blockquote className="border-foreground/20 my-7 border-l-2 pl-5 sm:my-10 sm:pl-6">
      <p className="text-foreground mb-3 text-xl leading-relaxed font-medium italic">
        &ldquo;{text}&rdquo;
      </p>
      <cite className="text-muted-foreground text-sm not-italic">{attribution}</cite>
    </blockquote>
  );
}

export function HighlightCards({
  highlights,
}: {
  highlights: Array<{ title: string; description: string }>;
}) {
  return (
    <div className="my-10 grid gap-2 sm:grid-cols-2 sm:gap-4">
      {highlights.map((h) => (
        <div
          key={h.title}
          className="rounded-xl p-5"
          style={{
            background: "color-mix(in srgb, var(--surface-1) 45%, var(--background))",
            border: "1px solid color-mix(in srgb, var(--foreground) 7%, transparent)",
          }}
        >
          <p className="text-foreground mb-1.5 text-sm font-semibold">{h.title}</p>
          <p className="text-muted-foreground text-xs leading-relaxed">{h.description}</p>
        </div>
      ))}
    </div>
  );
}

export function BenefitsGrid({
  benefits,
}: {
  benefits: Array<{ value: string; label: string; description: string }>;
}) {
  return (
    <div
      className="my-12 overflow-hidden rounded-2xl border"
      style={{
        borderColor: "color-mix(in srgb, var(--foreground) 6%, transparent)",
        background:
          "linear-gradient(135deg, color-mix(in srgb, var(--surface-1) 45%, var(--background)) 0%, var(--background) 60%, rgba(16,185,129,0.12) 100%)",
      }}
    >
      <div className="border-foreground/6 border-b px-8 py-6">
        <p className="text-muted-foreground text-xs font-semibold">Program Benefits</p>
      </div>
      <div
        className="divide-foreground/6 grid divide-y sm:divide-x sm:divide-y-0"
        style={{ gridTemplateColumns: `repeat(${Math.min(benefits.length, 3)}, 1fr)` }}
      >
        {benefits.map((b) => (
          <div key={b.value} className="flex flex-col gap-1.5 px-8 py-7">
            <span className="text-foreground text-4xl font-black tracking-tight">{b.value}</span>
            <span className="text-foreground text-sm font-semibold">{b.label}</span>
            <span className="text-muted-foreground text-xs leading-relaxed">{b.description}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function RequirementsList({ requirements }: { requirements: string[] }) {
  return (
    <ul className="my-6 list-none space-y-3 pl-0">
      {requirements.map((req) => (
        <li key={req} className="flex items-start gap-3">
          <div
            className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
            style={{
              background: "color-mix(in srgb, var(--foreground) 7%, transparent)",
              border: "1px solid color-mix(in srgb, var(--foreground) 12%, transparent)",
            }}
          >
            <Check size={10} className="text-foreground" />
          </div>
          <span className="text-muted-foreground text-sm leading-relaxed">{req}</span>
        </li>
      ))}
    </ul>
  );
}

export { ShareButton };
